import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LayoutDashboard, Users as UsersI, Building2, UserSquare2, FileText, Inbox as InboxI, MessageSquare, CalendarDays, Newspaper, Briefcase, HelpCircle, CalendarCheck, Mail, Send, Settings, ScrollText, DatabaseBackup, Download, FolderTree, ShieldCheck, Shield, MailOpen } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, RequireAuth, useAuth } from "./lib/auth";
import SiteLayout from "./components/site/SiteLayout";
import AppShell, { PORTAL_NAV } from "./components/site/AppShell";
import Home from "./pages/public/Home";
import { Studio, Servizi, News, NewsDetail } from "./pages/public/Content";
import { Scadenze, Faq, Contatti, Appuntamento, DatiPersonali, Legal, GuidaPortale, NotFound } from "./pages/public/Forms";
import { Newsletter, NewsletterConfirm, NewsletterManage } from "./pages/public/Newsletter";
import { Login, Forgot, SetPassword, ForcedMfa } from "./pages/auth/Auth";
import Security, { Preferences } from "./pages/portal/Security";
import { Dashboard, Documents, Requests, Messages, Deadlines, Appointments } from "./pages/portal/Portal";
import { TeamAdmin, NewsAdmin, ServicesAdmin, FaqAdmin, DeadlinesAdmin, CategoriesAdmin, CompaniesAdmin, Inbox, AuditLog } from "./pages/admin/Admin";
import { AdminDashboard, Users, DocumentsAdmin, RequestsAdmin, MessagesAdmin, SettingsAdmin, Backups, ExportAdmin, DemoMailbox } from "./pages/admin/AdminOps";
import Mailing from "./pages/admin/Mailing";

const ADMIN_NAV = [
  ["/admin", "Panoramica", LayoutDashboard, true, null], ["/admin/utenti", "Utenti e permessi", UsersI, false, "users"], ["/admin/aziende", "Aziende", Building2, false, "companies"],
  ["/admin/professionisti", "Professionisti", UserSquare2, false, "team"], ["/admin/collaboratori", "Collaboratori", UserSquare2, false, "team"],
  ["/admin/documenti", "Documenti", FileText, false, "documents"], ["/admin/categorie", "Categorie documenti", FolderTree, false, "documents"],
  ["/admin/richieste", "Richieste documentali", InboxI, false, "requests"], ["/admin/comunicazioni", "Comunicazioni", MessageSquare, false, "messages"],
  ["/admin/appuntamenti", "Appuntamenti", CalendarCheck, false, "appointments"], ["/admin/contatti", "Messaggi dal sito", Mail, false, "contacts"],
  ["/admin/news", "News e circolari", Newspaper, false, "content"], ["/admin/scadenze", "Scadenze", CalendarDays, false, "content"],
  ["/admin/servizi", "Servizi", Briefcase, false, "content"], ["/admin/faq", "FAQ", HelpCircle, false, "content"],
  ["/admin/mailing", "Mailing list", Send, false, "mailing"], ["/admin/impostazioni", "Impostazioni sito", Settings, false, "content"],
  ["/admin/richieste-dati", "Richieste privacy", Shield, false, "users"], ["/admin/registro", "Registro attività", ScrollText, false, "audit"],
  ["/admin/esportazioni", "Esportazioni", Download, false, "export"], ["/admin/backup", "Backup", DatabaseBackup, false, "backup"],
  ["/admin/casella-demo", "Casella demo / invii", MailOpen, false, "users"], ["/admin/sicurezza", "La mia sicurezza", ShieldCheck, false, null],
];

function AdminShell() {
  const { user } = useAuth();
  const items = ADMIN_NAV.filter((i) => !i[4] || user.permissions.includes(i[4])).map(([a, b, c, d]) => [a, b, c, d]);
  return <AppShell items={items} label="Pannello studio" />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<SiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/lo-studio" element={<Studio />} />
            <Route path="/servizi" element={<Servizi />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsDetail />} />
            <Route path="/scadenze" element={<Scadenze />} />
            <Route path="/domande-frequenti" element={<Faq />} />
            <Route path="/contatti" element={<Contatti />} />
            <Route path="/appuntamento" element={<Appuntamento />} />
            <Route path="/richieste-dati" element={<DatiPersonali />} />
            <Route path="/privacy" element={<Legal kind="privacy" />} />
            <Route path="/cookie" element={<Legal kind="cookie" />} />
            <Route path="/guida-portale" element={<GuidaPortale />} />
            <Route path="/newsletter" element={<Newsletter />} />
            <Route path="/newsletter/conferma" element={<NewsletterConfirm />} />
            <Route path="/newsletter/gestisci" element={<NewsletterManage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/accedi" element={<Login />} />
          <Route path="/password-dimenticata" element={<Forgot />} />
          <Route path="/reimposta-password" element={<SetPassword mode="reset" />} />
          <Route path="/attiva-account" element={<SetPassword mode="activate" />} />
          <Route path="/configura-mfa" element={<ForcedMfa />} />
          <Route path="/area-clienti" element={<RequireAuth kind="client"><AppShell items={PORTAL_NAV} label="Area clienti" /></RequireAuth>}>
            <Route index element={<Dashboard />} />
            <Route path="documenti" element={<Documents />} />
            <Route path="richieste" element={<Requests />} />
            <Route path="comunicazioni" element={<Messages />} />
            <Route path="scadenze" element={<Deadlines />} />
            <Route path="appuntamenti" element={<Appointments />} />
            <Route path="sicurezza" element={<Security />} />
            <Route path="preferenze" element={<Preferences />} />
          </Route>
          <Route path="/admin" element={<RequireAuth kind="staff"><AdminShell /></RequireAuth>}>
            <Route index element={<AdminDashboard />} />
            <Route path="utenti" element={<Users />} />
            <Route path="aziende" element={<CompaniesAdmin />} />
            <Route path="professionisti" element={<TeamAdmin kind="professional" />} />
            <Route path="collaboratori" element={<TeamAdmin kind="collaborator" />} />
            <Route path="documenti" element={<DocumentsAdmin />} />
            <Route path="categorie" element={<CategoriesAdmin />} />
            <Route path="richieste" element={<RequestsAdmin />} />
            <Route path="comunicazioni" element={<MessagesAdmin />} />
            <Route path="appuntamenti" element={<Inbox kind="appointments" />} />
            <Route path="contatti" element={<Inbox kind="contacts" />} />
            <Route path="richieste-dati" element={<Inbox kind="data" />} />
            <Route path="news" element={<NewsAdmin />} />
            <Route path="scadenze" element={<DeadlinesAdmin />} />
            <Route path="servizi" element={<ServicesAdmin />} />
            <Route path="faq" element={<FaqAdmin />} />
            <Route path="mailing" element={<Mailing />} />
            <Route path="impostazioni" element={<SettingsAdmin />} />
            <Route path="registro" element={<AuditLog />} />
            <Route path="esportazioni" element={<ExportAdmin />} />
            <Route path="backup" element={<Backups />} />
            <Route path="casella-demo" element={<DemoMailbox />} />
            <Route path="sicurezza" element={<Security />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}
